import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Table,
  Box,
  Text,
  TextInput,
  Checkbox,
  Badge,
  Group,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Menu,
  Button,
  Stack,
  Divider,
  MultiSelect,
  TagsInput,
  Textarea,
  Select,
  Modal,
  Code,
  Loader,
  Alert,
  Paper,
} from '@mantine/core';
import {
  IconEdit,
  IconSettings,
  IconCheck,
  IconX,
  IconFunction,
  IconVariable,
  IconLayersLinked,
  IconPlus,
  IconMinus,
} from '@tabler/icons-react';
import { BaseRecord, TemplateDoc, FieldOverride, FieldWithOverride, DefaultArrayItem } from '../../../types/geneva-schema';
import { GenevaFieldRenderer } from '../../../components/GenevaFieldRenderer';
import { separateArrayFields, getArrayFieldInfo, resolveFieldOverridesRecursively, resolveMultiRecordFieldOverrides, getInheritanceChain } from '../../../utils/geneva-schema-helpers';



interface GenevaFieldsTableProps {
  record: BaseRecord;
  template: TemplateDoc;
  onFieldOverrideChange: (recordName: string, fieldXmlTag: string, override: FieldOverride) => void;
  onFieldSelect: (fieldXmlTag: string) => void;
  // Multi-record specific props
  isMultiRecord?: boolean;
  virtualRecordId?: string;
  baseRecordName?: string;
}

export const GenevaFieldsTable: React.FC<GenevaFieldsTableProps> = ({
  record,
  template,
  onFieldOverrideChange,
  onFieldSelect,
  isMultiRecord = false,
  virtualRecordId,
  baseRecordName,
}) => {
  const [filterQuery, setFilterQuery] = useState('');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempOverride, setTempOverride] = useState<FieldOverride>({});
  const [computedModal, setComputedModal] = useState(false);
  const [computedField, setComputedField] = useState<string | null>(null);
  const [computedExpression, setComputedExpression] = useState('');
  const [arrayItemsModal, setArrayItemsModal] = useState(false);
  const [selectedArrayGroup, setSelectedArrayGroup] = useState<string | null>(null);

  const { arrayFieldGroups } = useMemo(() => {
    return separateArrayFields(record);
  }, [record]);

  const fieldsWithOverrides = useMemo((): FieldWithOverride[] => {
    // Use appropriate inheritance resolution based on context
    let recordOverrides: Record<string, FieldOverride>;

    if (isMultiRecord && virtualRecordId && baseRecordName) {
      // For multi-record configurations, use the specialized resolver
      recordOverrides = resolveMultiRecordFieldOverrides(virtualRecordId, baseRecordName, template);
    } else {
      // For regular records (including single virtual records), use standard resolution
      recordOverrides = resolveFieldOverridesRecursively(record.record, template);
    }

    // Debug: log all overrides for this record
    if (Object.keys(recordOverrides).some(key => key.includes('Price'))) {
      console.log(`[${record.record}] Overrides containing "Price":`,
        Object.fromEntries(Object.entries(recordOverrides).filter(([key]) => key.includes('Price')))
      );
    }

    // Include ALL fields (regular + array fields) as individual rows
    return record.fields.map(field => {
      const arrayInfo = getArrayFieldInfo(field);

      // Create composite key for array fields to avoid conflicts with regular fields
      // e.g., "NonCapExpenses.Amount" for array fields vs just "Amount" for regular fields
      const overrideKey = arrayInfo
        ? `${arrayInfo.arrayName}.${field.xmlTag}`
        : field.xmlTag;

      // Look up override using the exact key (no fallback to avoid conflicts)
      // For array fields: look up "ArrayName.Field"
      // For regular fields: look up "Field"
      const override = recordOverrides[overrideKey];

      // Debug: log Price field lookups
      if (field.xmlTag === 'Price') {
        console.log(`Field "${field.xmlTag}" (array: ${arrayInfo?.arrayName || 'none'}):`, {
          overrideKey,
          override,
          allKeys: Object.keys(recordOverrides)
        });
      }

      return {
        ...field,
        xmlTag: overrideKey, // Use composite key for both display and storage
        originalXmlTag: field.xmlTag, // Keep original xmlTag for reference
        override,
        isOverridden: !!override && Object.keys(override).length > 0,
        arrayGroup: arrayInfo?.arrayName,
        description: arrayInfo
          ? `[${arrayInfo.arrayName}] ${field.description || field.xmlTag}`
          : field.description,
      };
    });
  }, [record, template, isMultiRecord, virtualRecordId, baseRecordName]);

  const filteredFields = useMemo(() => {
    if (!filterQuery.trim()) return fieldsWithOverrides;

    const query = filterQuery.toLowerCase();
    return fieldsWithOverrides.filter(field =>
      field.xmlTag.toLowerCase().includes(query) ||
      field.description?.toLowerCase().includes(query) ||
      field.type.toLowerCase().includes(query) ||
      field.links?.some(link => link.toLowerCase().includes(query))
    );
  }, [fieldsWithOverrides, filterQuery]);

  const handleStartEdit = useCallback((field: FieldWithOverride) => {
    setEditingField(field.xmlTag);
    setTempOverride(field.override || {});
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editingField) return;

    // Remove empty/undefined values
    const cleanOverride = Object.fromEntries(
      Object.entries(tempOverride).filter(([_, value]) =>
        value !== undefined && value !== '' && value !== null
      )
    );

    // Use the composite key (editingField) which is already correct for both regular and array fields
    onFieldOverrideChange(record.record, editingField, cleanOverride);
    setEditingField(null);
    setTempOverride({});
  }, [editingField, tempOverride, onFieldOverrideChange, record.record]);

  const handleCancelEdit = useCallback(() => {
    setEditingField(null);
    setTempOverride({});
  }, []);

  const handleQuickToggle = useCallback((field: FieldWithOverride, key: keyof FieldOverride, value: any) => {
    const currentOverride = field.override || {};
    const newOverride = { ...currentOverride };

    // Remove the key if setting to false/empty, otherwise set the value
    if (!value || value === false || value === undefined) {
      delete newOverride[key];
    } else {
      newOverride[key] = value;
    }

    // Debug logging
    console.log(`[handleQuickToggle] Field: "${field.xmlTag}", Key: "${key}", Value:`, value);
    console.log(`[handleQuickToggle] Current override:`, currentOverride);
    console.log(`[handleQuickToggle] New override:`, newOverride);
    console.log(`[handleQuickToggle] Saving to record: "${record.record}", key: "${field.xmlTag}"`);

    // Use the composite key (field.xmlTag) which is already correct for both regular and array fields
    onFieldOverrideChange(record.record, field.xmlTag, newOverride);
  }, [onFieldOverrideChange, record.record]);


  const handleOpenComputedModal = useCallback((field: FieldWithOverride) => {
    setComputedField(field.xmlTag);
    setComputedExpression(field.override?.computed?.expr || '');
    setComputedModal(true);
  }, []);

  const handleSaveComputed = useCallback(() => {
    if (!computedField) return;

    const field = fieldsWithOverrides.find(f => f.xmlTag === computedField);
    if (!field) return;

    const currentOverride = field.override || {};
    const newOverride = { ...currentOverride };

    if (computedExpression.trim()) {
      newOverride.computed = { expr: computedExpression.trim() };
      console.log('Saving computed expression:', { field: computedField, expr: computedExpression.trim() });
    } else {
      delete newOverride.computed;
      console.log('Removing computed expression for field:', computedField);
    }

    // Use the composite key (computedField) which is already correct for both regular and array fields
    onFieldOverrideChange(record.record, computedField, newOverride);
    setComputedModal(false);
    setComputedField(null);
    setComputedExpression('');
  }, [computedField, computedExpression, fieldsWithOverrides, onFieldOverrideChange, record.record]);

  const getComputedPresets = useCallback(() => {
    const availableFields = record.fields.map(f => ({ value: f.xmlTag, label: f.xmlTag }));

    return [
      {
        label: 'Simple field references',
        options: availableFields.map(f => ({
          label: `Return ${f.label}`,
          value: `return ${f.value};`,
        })),
      },
      {
        label: 'Common patterns',
        options: [
          { label: 'Combine with underscore', value: 'return NameSort + "_" + Code;' },
          { label: 'Add suffix', value: 'return NameSort + "_COPY";' },
          { label: 'Add prefix', value: 'return "PREFIX_" + NameSort;' },
          { label: 'Uppercase', value: 'return NameSort.toUpperCase();' },
          { label: 'Current timestamp', value: 'return Date.now();' },
          { label: 'Format date', value: 'return new Date(EventDate).toLocaleString();' },
          { label: 'Add 3 days to date', value: 'return Date.addDays(EventDate, 3);' },
        ],
      },
      {
        label: 'Advanced examples',
        options: [
          {
            label: 'Conditional logic',
            value: `if (Price > 100) {
  return "EXPENSIVE";
} else {
  return "AFFORDABLE";
}`
          },
          {
            label: 'Calculate percentage',
            value: `return Math.round((Price / Quantity) * 100) + "%";`
          },
          {
            label: 'Format currency',
            value: `return new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(Price);`
          },
        ],
      },
    ];
  }, [record.fields]);

  const handleArrayItemsConfigOpen = useCallback((arrayGroup: string) => {
    setSelectedArrayGroup(arrayGroup);
    setArrayItemsModal(true);
  }, []);

  const handleArrayItemsConfigSave = useCallback((arrayGroup: string, defaultItems: DefaultArrayItem[]) => {
    // Find the first field in this array group and set the defaultArrayItems on it
    const arrayGroupObj = arrayFieldGroups.find(group => group.arrayName === arrayGroup);
    if (!arrayGroupObj || !arrayGroupObj.fields[0]) return;

    const firstField = arrayGroupObj.fields[0];
    // Use composite key for array field
    const compositeKey = `${arrayGroup}.${firstField.xmlTag}`;
    const currentOverride = fieldsWithOverrides.find(f => f.xmlTag === compositeKey)?.override || {};

    const newOverride = {
      ...currentOverride,
      defaultArrayItems: defaultItems.length > 0 ? defaultItems : undefined,
    };

    onFieldOverrideChange(record.record, compositeKey, newOverride);
    setArrayItemsModal(false);
    setSelectedArrayGroup(null);
  }, [arrayFieldGroups, fieldsWithOverrides, onFieldOverrideChange, record.record]);

  const bulkActions = [
    {
      label: 'Hide all non-required',
      action: () => {
        fieldsWithOverrides
          .filter(f => !f.required)
          .forEach(f => handleQuickToggle(f, 'hidden', true));
      },
    },
    {
      label: 'Clear all overrides',
      action: () => {
        fieldsWithOverrides
          .filter(f => f.isOverridden)
          .forEach(f => onFieldOverrideChange(record.record, f.xmlTag, {}));
      },
    },
    {
      label: 'Set all optional',
      action: () => {
        fieldsWithOverrides
          .filter(f => f.required)
          .forEach(f => handleQuickToggle(f, 'required', false));
      },
    },
  ];

  return (
    <Stack gap="md">
      {/* Header with controls */}
      <Group justify="space-between">
        <Box>
          <Text size="lg" fw={600}>
            {record.record} Fields
          </Text>
          <Text size="sm" c="dimmed">
            {filteredFields.length} of {record.fields.length} fields
            {filteredFields.filter(f => f.isOverridden).length > 0 &&
              ` • ${filteredFields.filter(f => f.isOverridden).length} overridden`
            }
          </Text>
        </Box>

        <Group>
          <TextInput
            placeholder="Filter fields..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            size="sm"
            w={200}
          />

          <Menu>
            <Menu.Target>
              <Button variant="light" size="sm" leftSection={<IconSettings size={14} />}>
                Bulk Actions
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {bulkActions.map((action, index) => (
                <Menu.Item key={index} onClick={action.action}>
                  {action.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      <Divider />

      {/* Show inheritance chain for virtual records */}
      {(() => {
        try {
          let inheritanceChain: string[] = [];
          let inheritanceContext = '';

          if (isMultiRecord && virtualRecordId && baseRecordName) {
            // For multi-record, show the base record's inheritance chain
            inheritanceChain = getInheritanceChain(baseRecordName, template);
            // Add the virtual multi-record as the first item
            if (inheritanceChain.length === 1 && inheritanceChain[0] === baseRecordName) {
              inheritanceChain = [virtualRecordId, baseRecordName];
            } else if (!inheritanceChain.includes(virtualRecordId)) {
              inheritanceChain = [virtualRecordId, ...inheritanceChain];
            }
            inheritanceContext = `Multi-record configuration "${baseRecordName}" within "${template.virtualRecords?.[virtualRecordId]?.label || virtualRecordId}"`;
          } else {
            inheritanceChain = getInheritanceChain(record.record, template);
            inheritanceContext = 'Inheritance Chain';
          }

          if (inheritanceChain.length > 1) {
            return (
              <Alert icon={<IconLayersLinked size="1rem" />} color="blue" mb="md">
                <Text size="sm" fw={500} mb="xs">{inheritanceContext}:</Text>
                <Group gap="xs">
                  {inheritanceChain.map((recordId, index) => (
                    <React.Fragment key={recordId}>
                      <Badge
                        variant={index === 0 ? "filled" : "light"}
                        color={index === 0 ? "blue" : "gray"}
                        size="sm"
                      >
                        {template.virtualRecords?.[recordId]?.label || recordId}
                      </Badge>
                      {index < inheritanceChain.length - 1 && (
                        <Text size="xs" c="dimmed">→</Text>
                      )}
                    </React.Fragment>
                  ))}
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  Field overrides are inherited from parent records (rightmost takes precedence)
                  {isMultiRecord && '. Multi-record specific overrides have highest precedence.'}
                </Text>
              </Alert>
            );
          }
        } catch (error) {
          // If there's a circular reference or other error, show warning
          return (
            <Alert color="red" mb="md">
              <Text size="sm">
                ⚠️ Inheritance chain error: {error instanceof Error ? error.message : 'Unknown error'}
              </Text>
            </Alert>
          );
        }
        return null;
      })()}

      {/* Array Field Groups Info */}
      {arrayFieldGroups.length > 0 && (
        <Alert icon={<IconLayersLinked size="1rem" />} color="blue" mb="md">
          <Group justify="space-between" align="flex-start">
            <div style={{ flex: 1 }}>
              <Text size="sm" fw={500} mb="xs">Array Field Groups - Configure individual fields in the table below:</Text>
              <Group gap="xs">
                {arrayFieldGroups.map(group => {
                  const firstField = group.fields[0];
                  // Use composite key for array field
                  const compositeKey = `${group.arrayName}.${firstField?.xmlTag}`;
                  const fieldWithOverride = fieldsWithOverrides.find(f => f.xmlTag === compositeKey);
                  const hasDefaultItems = fieldWithOverride?.override?.defaultArrayItems && fieldWithOverride.override.defaultArrayItems.length > 0;

                  return (
                    <Badge
                      key={group.arrayName}
                      variant={hasDefaultItems ? "filled" : "light"}
                      color={hasDefaultItems ? "green" : "blue"}
                      mr="xs"
                    >
                      {group.arrayName} ({group.fields.length} fields)
                      {hasDefaultItems && ` • ${fieldWithOverride?.override?.defaultArrayItems?.length} defaults`}
                    </Badge>
                  );
                })}
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                Each array field can be individually configured with defaults, expressions, visibility, and validation.
                Configure default array items (like commission, fees) using the button on the right.
              </Text>
            </div>
            {arrayFieldGroups.length === 1 ? (
              <Button
                size="sm"
                variant="light"
                leftSection={<IconLayersLinked size={16} />}
                onClick={() => handleArrayItemsConfigOpen(arrayFieldGroups[0].arrayName)}
              >
                Configure Default Items
              </Button>
            ) : (
              <Menu>
                <Menu.Target>
                  <Button
                    size="sm"
                    variant="light"
                    leftSection={<IconLayersLinked size={16} />}
                  >
                    Configure Default Items
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {arrayFieldGroups.map(group => (
                    <Menu.Item
                      key={group.arrayName}
                      onClick={() => handleArrayItemsConfigOpen(group.arrayName)}
                    >
                      {group.arrayName} ({group.fields.length} fields)
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Alert>
      )}

      {/* Table */}
      <ScrollArea style={{ height: '60vh', width: '100%' }}>
        <Table striped highlightOnHover style={{ width: '100%', tableLayout: 'fixed' }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: '12%' }}>XML Tag</Table.Th>
              <Table.Th style={{ width: '8%' }}>Type</Table.Th>
              <Table.Th style={{ width: '4%' }}>Req</Table.Th>
              <Table.Th style={{ width: '4%' }}>Hidden</Table.Th>
              <Table.Th style={{ width: '4%' }}>R/O</Table.Th>
              <Table.Th style={{ width: '10%' }}>Default</Table.Th>
              <Table.Th style={{ width: '8%' }}>Options</Table.Th>
              <Table.Th style={{ width: '12%' }}>Computed</Table.Th>
              <Table.Th style={{ width: '28%' }}>Description</Table.Th>
              <Table.Th style={{ width: '10%' }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredFields.map((field) => (
              <FieldRow
                key={field.xmlTag}
                field={field}
                isEditing={editingField === field.xmlTag}
                tempOverride={tempOverride}
                onTempOverrideChange={setTempOverride}
                onStartEdit={() => handleStartEdit(field)}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onQuickToggle={(key, value) => handleQuickToggle(field, key, value)}
                onFieldSelect={() => onFieldSelect(field.originalXmlTag || field.xmlTag)}
                onComputedEdit={() => handleOpenComputedModal(field)}
              />
            ))}
          </Table.Tbody>
        </Table>

        {filteredFields.length === 0 && (
          <Box ta="center" py="xl">
            <Text c="dimmed">No fields match your filter</Text>
          </Box>
        )}
      </ScrollArea>

      {/* Computed Expression Modal */}
      <Modal
        opened={computedModal}
        onClose={() => {
          setComputedModal(false);
          setComputedField(null);
          setComputedExpression('');
        }}
        title={
          <Group gap="xs">
            <IconFunction size={16} />
            <Text>Set Computed Expression for {computedField}</Text>
          </Group>
        }
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Create a computed expression that automatically calculates this field's value based on other fields or constants.
          </Text>

          <Textarea
            label="Expression"
            placeholder={`// JavaScript expression - can use multiple lines
return NameSort + '_' + Code;

// Or simple field reference:
// EventDate

// Date operations:
// new Date(EventDate).toLocaleString()
// Date.addDays(EventDate, 3)`}
            value={computedExpression}
            onChange={(e) => setComputedExpression(e.target.value)}
            autosize
            minRows={6}
            maxRows={15}
            styles={{
              input: {
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "source-code-pro", monospace',
                fontSize: '13px',
                lineHeight: '1.4',
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                border: '1px solid #404040',
                borderRadius: '4px',
                padding: '12px',
                tabSize: 2,
                whiteSpace: 'pre',
                overflowWrap: 'normal',
              }
            }}
            onKeyDown={(e) => {
              // Handle Tab key for indentation
              if (e.key === 'Tab') {
                e.preventDefault();
                const textarea = e.currentTarget;
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const newValue = computedExpression.substring(0, start) + '  ' + computedExpression.substring(end);
                setComputedExpression(newValue);
                // Set cursor position after the inserted spaces
                setTimeout(() => {
                  textarea.selectionStart = textarea.selectionEnd = start + 2;
                }, 0);
              }
            }}
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>Quick Presets:</Text>
            <Group gap="xs">
              {getComputedPresets().map((category) =>
                category.options.map((preset, idx) => (
                  <Button
                    key={`${category.label}-${idx}`}
                    size="xs"
                    variant="light"
                    onClick={() => setComputedExpression(preset.value)}
                  >
                    {preset.label}
                  </Button>
                ))
              )}
            </Group>
          </Stack>

          <Box>
            <Text size="sm" fw={500} mb="xs">Examples:</Text>
            <Stack gap="xs">
              <Code block style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace', fontSize: '12px' }}>
                {`// Simple field reference
return EventDate;`}
              </Code>
              <Code block style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace', fontSize: '12px' }}>
                {`// Combine fields
return NameSort + "_" + Code;`}
              </Code>
              <Code block style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace', fontSize: '12px' }}>
                {`// Date operations
return new Date(EventDate).toLocaleString();`}
              </Code>
              <Code block style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace', fontSize: '12px' }}>
                {`// Multi-line logic
if (Price > 100) {
  return "EXPENSIVE";
} else {
  return "AFFORDABLE";
}`}
              </Code>
              <Code block style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace', fontSize: '12px' }}>
                {`// Date helpers
return Date.addDays(EventDate, 3);`}
              </Code>
            </Stack>
          </Box>

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                setComputedModal(false);
                setComputedField(null);
                setComputedExpression('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveComputed}>
              Save Expression
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Array Default Items Configuration Modal */}
      <ArrayDefaultItemsModal
        opened={arrayItemsModal}
        onClose={() => {
          setArrayItemsModal(false);
          setSelectedArrayGroup(null);
        }}
        arrayGroup={selectedArrayGroup}
        arrayFieldGroups={arrayFieldGroups}
        fieldsWithOverrides={fieldsWithOverrides}
        onSave={handleArrayItemsConfigSave}
      />
    </Stack>
  );
};

interface FieldRowProps {
  field: FieldWithOverride;
  isEditing: boolean;
  tempOverride: FieldOverride;
  onTempOverrideChange: (override: FieldOverride) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onQuickToggle: (key: keyof FieldOverride, value: any) => void;
  onFieldSelect: () => void;
  onComputedEdit: () => void;
}

const FieldRow: React.FC<FieldRowProps> = ({
  field,
  isEditing,
  tempOverride,
  onTempOverrideChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onQuickToggle,
  onFieldSelect,
  onComputedEdit,
}) => {
  const getDisplayValue = (key: keyof FieldOverride) => {
    if (isEditing) return tempOverride[key];
    return field.override?.[key];
  };

  const isRequired = (getDisplayValue('required') as boolean) ?? field.required;
  const isHidden = (getDisplayValue('hidden') as boolean) ?? false;
  const isReadOnly = (getDisplayValue('readOnly') as boolean) ?? false;
  const defaultValue = getDisplayValue('default');
  const options = (getDisplayValue('options') as string[]) ?? [];
  const computed = getDisplayValue('computed') as { expr: string } | undefined;

  return (
    <Table.Tr
      style={{
        backgroundColor: field.isOverridden ? 'var(--mantine-color-blue-0)' : undefined,
        opacity: isHidden ? 0.6 : 1,
      }}
    >
      <Table.Td style={{ overflow: 'hidden' }}>
        <Group gap="xs" wrap="nowrap">
          {field.arrayGroup && (
            <Badge size="xs" color="grape" variant="filled">
              ARRAY
            </Badge>
          )}
          <Text
            size="sm"
            fw={500}
            style={{ cursor: 'pointer', wordBreak: 'break-word' }}
            onClick={onFieldSelect}
            truncate
          >
            {field.arrayGroup ? field.originalXmlTag : field.xmlTag}
          </Text>
          {field.isOverridden && (
            <Badge size="xs" color="blue" variant="dot">
              Modified
            </Badge>
          )}
          {field.links && field.links.length > 0 && (
            <Badge size="xs" color="green" variant="outline">
              API
            </Badge>
          )}
        </Group>
      </Table.Td>

      <Table.Td style={{ overflow: 'hidden' }}>
        <Text size="sm" truncate title={field.type}>
          {field.type}
        </Text>
      </Table.Td>

      <Table.Td>
        <Checkbox
          checked={isRequired}
          onChange={(e) => onQuickToggle('required', e.target.checked ? true : undefined)}
          size="sm"
          disabled={isEditing}
        />
      </Table.Td>

      <Table.Td>
        <Checkbox
          checked={isHidden}
          onChange={(e) => onQuickToggle('hidden', e.target.checked ? true : undefined)}
          size="sm"
          disabled={isEditing}
        />
      </Table.Td>

      <Table.Td>
        <Checkbox
          checked={isReadOnly}
          onChange={(e) => onQuickToggle('readOnly', e.target.checked ? true : undefined)}
          size="sm"
          disabled={isEditing}
        />
      </Table.Td>

      <Table.Td style={{ overflow: 'hidden' }}>
        {isEditing ? (
          <GenevaFieldRenderer
            field={{
              ...field,
              override: tempOverride,
              isOverridden: !!tempOverride && Object.keys(tempOverride).length > 0,
            }}
            value={tempOverride.default}
            onChange={(_fieldName, value) => onTempOverrideChange({ ...tempOverride, default: value })}
            showLabel={false}
            showDescription={false}
            showOverrideBadges={false}
            placeholder="Default value"
          />
        ) : (
          <Text size="sm" truncate title={defaultValue ? String(defaultValue) : undefined}>
            {defaultValue ? String(defaultValue) : '-'}
          </Text>
        )}
      </Table.Td>

      <Table.Td style={{ overflow: 'hidden' }}>
        {isEditing ? (
          <TagsInput
            size="xs"
            value={tempOverride.options || []}
            onChange={(values) => onTempOverrideChange({ ...tempOverride, options: values })}
            placeholder="Type and press Enter"
            data={options} // Show existing options as suggestions
            style={{ maxWidth: '100%' }}
            splitChars={[',', ' ', '|']}
            clearable
          />
        ) : (
          <Text size="sm" truncate title={options?.length ? `${options.length} options` : undefined}>
            {options?.length ? `${options.length} options` : '-'}
          </Text>
        )}
      </Table.Td>

      {/* Computed Expression Column */}
      <Table.Td style={{ overflow: 'hidden' }}>
        <Group gap="xs" wrap="nowrap">
          {computed ? (
            <>
              <IconFunction size={14} color="var(--mantine-color-violet-6)" />
              <Code style={{ maxWidth: '100%', overflow: 'hidden', fontSize: '0.75rem' }}>
                {computed.expr.length > 20 ? `${computed.expr.substring(0, 20)}...` : computed.expr}
              </Code>
            </>
          ) : (
            <ActionIcon
              size="xs"
              variant="subtle"
              onClick={onComputedEdit}
              disabled={isEditing}
            >
              <IconVariable size={12} />
            </ActionIcon>
          )}
          {computed && (
            <ActionIcon
              size="xs"
              variant="subtle"
              onClick={onComputedEdit}
              disabled={isEditing}
            >
              <IconEdit size={12} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>

      <Table.Td style={{ overflow: 'hidden', wordWrap: 'break-word' }}>
        <Text
          size="sm"
          style={{
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            lineHeight: 1.3,
            maxHeight: '4.5em',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {field.description || '-'}
        </Text>
      </Table.Td>

      <Table.Td>
        <Group gap="xs">
          {isEditing ? (
            <>
              <ActionIcon size="sm" onClick={onSaveEdit} color="green">
                <IconCheck size={14} />
              </ActionIcon>
              <ActionIcon size="sm" onClick={onCancelEdit} color="red">
                <IconX size={14} />
              </ActionIcon>
            </>
          ) : (
            <>
              <ActionIcon size="sm" onClick={onStartEdit} variant="subtle">
                <IconEdit size={14} />
              </ActionIcon>
              <ActionIcon size="sm" onClick={onFieldSelect} variant="subtle">
                <IconSettings size={14} />
              </ActionIcon>
            </>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

// Array Default Items Configuration Modal Component
interface ArrayDefaultItemsModalProps {
  opened: boolean;
  onClose: () => void;
  arrayGroup: string | null;
  arrayFieldGroups: any[];
  fieldsWithOverrides: FieldWithOverride[];
  onSave: (arrayGroup: string, defaultItems: DefaultArrayItem[]) => void;
}

const ArrayDefaultItemsModal: React.FC<ArrayDefaultItemsModalProps> = ({
  opened,
  onClose,
  arrayGroup,
  arrayFieldGroups,
  fieldsWithOverrides,
  onSave,
}) => {
  const [defaultItems, setDefaultItems] = useState<DefaultArrayItem[]>([]);

  // Get the array group object
  const arrayGroupObj = arrayFieldGroups.find(group => group.arrayName === arrayGroup);

  // Initialize form data when modal opens
  React.useEffect(() => {
    if (!arrayGroup || !arrayGroupObj || !opened) return;

    // Find existing default items from the first field's override
    const firstField = arrayGroupObj.fields[0];
    // Use composite key for array field
    const compositeKey = `${arrayGroup}.${firstField?.xmlTag}`;
    const fieldWithOverride = fieldsWithOverrides.find(f => f.xmlTag === compositeKey);
    const existingDefaults = fieldWithOverride?.override?.defaultArrayItems || [];
    const normalizedDefaults = existingDefaults.map((item, idx) => {
      const clonedFieldSettings = item.fieldSettings
        ? Object.fromEntries(
            Object.entries(item.fieldSettings).map(([fieldName, settings]) => [
              fieldName,
              { ...settings },
            ])
          )
        : {};

      return {
        ...item,
        id: item.id || `${arrayGroup}-${idx}`,
        values: { ...(item.values || {}) },
        computedExpressions: item.computedExpressions ? { ...item.computedExpressions } : {},
        fieldSettings: clonedFieldSettings,
      };
    });

    setDefaultItems(normalizedDefaults);
  }, [arrayGroup, arrayGroupObj, fieldsWithOverrides, opened]);

  const addDefaultItem = useCallback(() => {
    if (!arrayGroupObj) return;

    // Create a default item with all field names from the array group
    const values: Record<string, unknown> = {};
    arrayGroupObj.fields.forEach((field: any) => {
      const arrayInfo = getArrayFieldInfo(field);
      if (arrayInfo) {
        values[arrayInfo.fieldName] = '';
      }
    });

    const newItemId = `${arrayGroup}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    const newItem: DefaultArrayItem = {
      id: newItemId,
      values,
      computedExpressions: {},
      fieldSettings: {},
      label: `Default Item ${defaultItems.length + 1}`,
    };

    setDefaultItems(prev => [...prev, newItem]);
  }, [arrayGroupObj, defaultItems.length]);

  const removeDefaultItem = useCallback((index: number) => {
    setDefaultItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateDefaultItem = useCallback((index: number, fieldName: string, value: unknown) => {
    setDefaultItems(prev => prev.map((item, i) =>
      i === index ? { ...item, values: { ...item.values, [fieldName]: value } } : item
    ));
  }, []);

  const updateDefaultItemExpression = useCallback((index: number, fieldName: string, expression: string) => {
    setDefaultItems(prev => prev.map((item, i) => {
      if (i !== index) return item;

      const newExpressions = { ...item.computedExpressions };
      if (expression.trim()) {
        newExpressions[fieldName] = expression;
      } else {
        delete newExpressions[fieldName];
      }

      return { ...item, computedExpressions: newExpressions };
    }));
  }, []);

  const updateDefaultItemLabel = useCallback((index: number, label: string) => {
    setDefaultItems(prev => prev.map((item, i) =>
      i === index ? { ...item, label } : item
    ));
  }, []);

  const updateDefaultItemFieldSetting = useCallback((
    index: number,
    fieldName: string,
    setting: 'required' | 'hidden' | 'readOnly',
    value: boolean
  ) => {
    setDefaultItems(prev => prev.map((item, i) => {
      if (i !== index) return item;

      const fieldSettings = { ...(item.fieldSettings || {}) };
      const currentSettings = { ...(fieldSettings[fieldName] || {}) };

      if (value) {
        currentSettings[setting] = true;
      } else {
        delete currentSettings[setting];
      }

      if (Object.keys(currentSettings).length === 0) {
        delete fieldSettings[fieldName];
      } else {
        fieldSettings[fieldName] = currentSettings;
      }

      return { ...item, fieldSettings };
    }));
  }, []);

  const handleSave = useCallback(() => {
    if (!arrayGroup) return;
    onSave(arrayGroup, defaultItems);
  }, [arrayGroup, defaultItems, onSave]);

  if (!arrayGroup || !arrayGroupObj) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconLayersLinked size={16} />
          <Text>Configure Default Items: {arrayGroup}</Text>
        </Group>
      }
      size="lg"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Configure default items that will be automatically added when this array field is first loaded.
          For example, you can create one item for "Commission" and another for "Other Fees".
          Use computed expressions on individual fields to calculate values.
        </Text>

        {/* Default Array Items */}
        <div>
          <Group justify="space-between" mb="xs">
            <div>
              <Text size="sm" fw={500}>Default Array Items</Text>
              <Text size="xs" c="dimmed">
                Pre-populate the array with default values when the form loads
              </Text>
            </div>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={addDefaultItem}
            >
              Add Default Item
            </Button>
          </Group>

          <Stack gap="xs">
            {defaultItems.map((item, index) => (
              <Paper key={index} withBorder p="sm">
                <Group justify="space-between" mb="xs">
                  <TextInput
                    size="xs"
                    placeholder={`Item label (e.g., "Commission", "Other Fees")`}
                    value={item.label || ''}
                    onChange={(e) => updateDefaultItemLabel(index, e.target.value)}
                    style={{ flexGrow: 1, maxWidth: '300px' }}
                  />
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => removeDefaultItem(index)}
                  >
                    <IconMinus size={14} />
                  </ActionIcon>
                </Group>

                <Stack gap="xs">
                  {arrayGroupObj.fields.map((field: any) => {
                    const arrayInfo = getArrayFieldInfo(field);
                    if (!arrayInfo) return null;
                    const fieldSettings = item.fieldSettings?.[arrayInfo.fieldName] || {};
                    const isRequired = !!fieldSettings.required;
                    const isHidden = !!fieldSettings.hidden;
                    const isReadOnly = !!fieldSettings.readOnly;
                    const hasExpression = item.computedExpressions?.[arrayInfo.fieldName];

                    return (
                      <div key={field.xmlTag}>
                        <Text size="xs" fw={500} mb="xs">
                          {arrayInfo.fieldName} ({field.type})
                        </Text>
                        <Group gap="md" mb="xs">
                          <Checkbox
                            size="xs"
                            label="Req"
                            checked={isRequired}
                            onChange={(e) => updateDefaultItemFieldSetting(index, arrayInfo.fieldName, 'required', e.currentTarget.checked)}
                          />
                          <Checkbox
                            size="xs"
                            label="Hidden"
                            checked={isHidden}
                            onChange={(e) => updateDefaultItemFieldSetting(index, arrayInfo.fieldName, 'hidden', e.currentTarget.checked)}
                          />
                          <Checkbox
                            size="xs"
                            label="R/O"
                            checked={isReadOnly}
                            onChange={(e) => updateDefaultItemFieldSetting(index, arrayInfo.fieldName, 'readOnly', e.currentTarget.checked)}
                          />
                        </Group>
                        <Group gap="xs" align="flex-end">
                          <div style={{ flex: 1 }}>
                            <GenevaFieldRenderer
                              field={{
                                ...field,
                                xmlTag: arrayInfo.fieldName,
                                isOverridden: false,
                              }}
                              value={item.values[arrayInfo.fieldName]}
                              onChange={(_fieldName, value) => updateDefaultItem(index, arrayInfo.fieldName, value)}
                              readOnly={!!hasExpression}
                              showLabel={true}
                              showDescription={false}
                              showOverrideBadges={false}
                              placeholder={hasExpression ? "Computed" : `Enter ${field.type}`}
                            />
                            {hasExpression && (
                              <Text size="xs" c="dimmed" mt={2}>Value will be computed</Text>
                            )}
                          </div>
                          <Textarea
                            label="Expression"
                            placeholder={`e.g., "return Price * 0.${index === 0 ? '10' : '50'}" (${index === 0 ? '10%' : '50%'})`}
                            value={item.computedExpressions?.[arrayInfo.fieldName] || ''}
                            onChange={(e) => updateDefaultItemExpression(index, arrayInfo.fieldName, e.target.value)}
                            size="xs"
                            autosize
                            minRows={1}
                            maxRows={3}
                            style={{ flex: 2 }}
                          />
                        </Group>
                      </div>
                    );
                  })}
                </Stack>
              </Paper>
            ))}

            {defaultItems.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No default items configured. Click "Add Default Item" to add some.
              </Text>
            )}
          </Stack>
        </div>

        <Alert color="blue" variant="light">
          <Text size="xs">
            <strong>How it works:</strong> Each default item can have its own computed expressions for different fee calculations.
            <br />
            <strong>Example:</strong> Create "Commission" item with expression <code>return Price * 0.10</code> (10%) and
            "Other Fees" item with expression <code>return Price * 0.50</code> (50%) on the Amount field.
            <br />
            <strong>Context:</strong> Expressions have access to all form fields (like Price, Quantity) and other array item values.
            <br />
            <strong>Note:</strong> If expressions reference fields that don't exist yet (like Price), they'll use static values initially and recalculate when those fields are filled.
          </Text>
        </Alert>

        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Default Items
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}; 
